import {
  Entity,
  RELATION_PARENT_OF,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { getEntityRelations } from '@backstage/plugin-catalog-react';

export const OWNERS_QUERY_PARAM = 'owners';
export const OWNER_SCOPE_REFS_QUERY_PARAM = 'ownerScopeRefs';
export const SELECTED_OWNERS_STORAGE_KEY = 'pulse.insights.selected-owners';

export const toCanonicalOwnerRef = (entityRef: string): string => {
  try {
    return stringifyEntityRef(
      parseEntityRef(entityRef, {
        defaultKind: 'group',
        defaultNamespace: 'default',
      }),
    ).toLowerCase();
  } catch {
    return entityRef.trim().toLowerCase();
  }
};

const childOwnerRefs = (entity: Entity | undefined) =>
  getEntityRelations(entity, RELATION_PARENT_OF, { kind: 'group' })
    .map(stringifyEntityRef)
    .map(toCanonicalOwnerRef);

export const getDescendantOwnerRefs = (
  selectedOwnerRef: string,
  ownerByRef: Map<string, Entity>,
): string[] => {
  const rootRef = toCanonicalOwnerRef(selectedOwnerRef);
  // Seeded with the root so a cycle back to it is not reported as its own descendant.
  const discovered = new Set([rootRef]);
  const stack = [...childOwnerRefs(ownerByRef.get(rootRef))];

  while (stack.length > 0) {
    const next = stack.pop();
    if (!next || discovered.has(next)) {
      continue;
    }

    discovered.add(next);

    for (const child of childOwnerRefs(ownerByRef.get(next))) {
      if (!discovered.has(child)) {
        stack.push(child);
      }
    }
  }

  discovered.delete(rootRef);
  return [...discovered];
};

export const sortRefs = (refs: string[]): string[] => {
  return [...refs].sort((left, right) => left.localeCompare(right));
};

/**
 * Expands selected groups to include their descendants, which is the owner
 * scope the insights endpoint aggregates over.
 *
 * Returns empty until `ownerByRef` is populated so callers do not fetch against
 * an unexpanded scope while the catalog groups are still loading.
 */
export const expandOwnerScopeRefs = (
  selectedOwnerRefs: string[],
  ownerByRef: Map<string, Entity>,
): string[] => {
  if (selectedOwnerRefs.length === 0 || ownerByRef.size === 0) {
    return [];
  }

  const scopedGroupRefs = new Set<string>();
  for (const selectedGroupRef of selectedOwnerRefs) {
    scopedGroupRefs.add(selectedGroupRef);
    for (const descendant of getDescendantOwnerRefs(
      selectedGroupRef,
      ownerByRef,
    )) {
      scopedGroupRefs.add(descendant);
    }
  }

  return sortRefs([...scopedGroupRefs]);
};

export const parseOwnerParamsFromUrl = (
  initialOwnerRefs: string[] = [],
): {
  selectedOwnerRefs: string[];
  hasExplicitSelection: boolean;
} => {
  if (globalThis.window === undefined) {
    return {
      selectedOwnerRefs: [],
      hasExplicitSelection: false,
    };
  }

  const params = new URLSearchParams(globalThis.window.location.search);
  const selectedOwnerRefsFromUrl = [
    ...new Set(
      params
        .getAll(OWNERS_QUERY_PARAM)
        .flatMap(value => value.split(','))
        .map(ownerRef => ownerRef.trim())
        .filter(ownerRef => ownerRef.length > 0)
        .map(toCanonicalOwnerRef),
    ),
  ];

  // If URL params exist, they take precedence
  if (selectedOwnerRefsFromUrl.length > 0) {
    return {
      selectedOwnerRefs: selectedOwnerRefsFromUrl,
      hasExplicitSelection: true,
    };
  }

  const selectedInitialOwnerRefs = [
    ...new Set(initialOwnerRefs.map(toCanonicalOwnerRef)),
  ];
  if (selectedInitialOwnerRefs.length > 0) {
    return {
      selectedOwnerRefs: selectedInitialOwnerRefs,
      hasExplicitSelection: true,
    };
  }

  // Try localStorage next
  try {
    const stored = globalThis.localStorage.getItem(SELECTED_OWNERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        const selectedOwnerRefs = [
          ...new Set(
            parsed
              .filter(item => typeof item === 'string')
              .map(item => item.trim())
              .filter(item => item.length > 0)
              .map(toCanonicalOwnerRef),
          ),
        ];
        return {
          selectedOwnerRefs,
          hasExplicitSelection: true,
        };
      }
    }
  } catch {
    // Fall through to default
  }

  // No saved state found - this is first visit
  return {
    selectedOwnerRefs: [],
    hasExplicitSelection: false,
  };
};

export const syncOwnerParamsToUrl = (selectedOwnerRefs: string[]) => {
  if (globalThis.window === undefined) {
    return;
  }

  const url = new URL(globalThis.window.location.href);
  url.searchParams.delete(OWNERS_QUERY_PARAM);
  // Remove legacy expanded-scope params that were previously written to the URL.
  url.searchParams.delete(OWNER_SCOPE_REFS_QUERY_PARAM);

  if (selectedOwnerRefs.length > 0) {
    url.searchParams.set(OWNERS_QUERY_PARAM, selectedOwnerRefs.join(','));
  }

  globalThis.window.history.replaceState({}, '', url.toString());
};
